import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Package2, Printer, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice, getDomain, displayPriceWithCurrency } from '@/lib/utils';
import { useStoreInfo } from '@/lib/store';

interface OrderDetail {
  payment_id: string;
  product_name: string;
  product_variant: string;
  variant_qty: number;
  status: string;
  product_amount: number;
}

interface PaymentInfo {
  store_id: number;
  order_id: string;
  date: string;
  goods_amount: number;
  shipping_fee: number;
  shipmentType: string;
  note: string;
}

interface OrderResponse {
  status: string;
  message: string;
  orderDetails?: OrderDetail[];
  payment_info?: PaymentInfo;
}

export function TrackPage() {
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderResponse | null>(null);
  const { storeInfo } = useStoreInfo();
  const pageTitle = `Track Order${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const domain = getDomain();
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/orderTracking.php?trackingId=${trackingId}&domain=${domain}`);
      const data: OrderResponse = await response.json();
      setOrderStatus(data);
      if (!data.status) {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch order status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Track your order status" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-brand-primary py-24">
          <div className="absolute inset-0">
            <img 
              src={`https://marivieint.com/${storeInfo?.heroSlider?.[1] || ''}`}
              alt="Tracking background"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display">
              Track Your Order
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Enter your order tracking number to check its status
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6 print:hidden">
              <div>
                <label
                  htmlFor="trackingId"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Tracking Number
                </label>
                <input
                  type="text"
                  id="trackingId"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  placeholder="Enter your tracking number"
                  className="w-full h-12 px-4 rounded-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? 'Tracking...' : 'Track Order'}
              </Button>
            </form>

            {error && (
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg print:hidden">
                {error}
              </div>
            )}

            {orderStatus?.status && orderStatus.orderDetails && orderStatus.orderDetails.length > 0 && (
              <div className="mt-8 space-y-6">
                <div className="flex justify-between items-center print:hidden">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Order Details
                  </h2>
                  <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2">
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 print:border print:border-gray-200">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Order ID</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {orderStatus.orderDetails[0].payment_id}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {orderStatus.orderDetails[0].status}
                      </p>
                    </div>
                  </div>

                  <div className="border-t dark:border-gray-600 pt-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Order Items</p>
                    <div className="space-y-4">
                      {orderStatus.orderDetails.map((item, index) => (
                        <div key={index} className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {item.product_name}
                            </p>
                            {item.product_variant !== 'Nil' && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Variant: {item.product_variant}
                              </p>
                            )}
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {item.product_amount} × {item.variant_qty}
                            </p>
                          </div>
                          <p className="text-gray-900 dark:text-white font-medium">
                            {displayPriceWithCurrency((item.product_amount * item.variant_qty).toString())}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {orderStatus.payment_info && (
                    <div className="border-t dark:border-gray-600 mt-6 pt-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                          <span className="text-gray-900 dark:text-white">
                            {displayPriceWithCurrency(orderStatus.payment_info.goods_amount.toString())}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Delivery Fee</span>
                          <span className="text-gray-900 dark:text-white">
                            {displayPriceWithCurrency(orderStatus.payment_info.shipping_fee.toString())}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Delivery Type</span>
                          <span className="text-gray-900 dark:text-white">
                            {orderStatus.payment_info.shipmentType}
                          </span>
                        </div>
                        <div className="flex justify-between font-medium pt-2 border-t dark:border-gray-600">
                          <span className="text-gray-900 dark:text-white">Total</span>
                          <span className="text-gray-900 dark:text-white">
                            {displayPriceWithCurrency((parseFloat(orderStatus.payment_info.goods_amount) + parseFloat(orderStatus.payment_info.shipping_fee)).toString())}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}